<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import type { ChildrenItem } from '~/composables/useModules';

    const props = defineProps<{
        content: ChildrenItem[];
    }>();

    const { tenant } = storeToRefs(useAuthStore());
    const { isUpsellRestricted, isCrossFamily } = useUpsellAccess();
    const { t } = useTranslation();

    const visibleLinks = computed(() => {
        return props.content.filter((link) => {
            if (link.key === 'subscription' && tenant.value?.subscriptionPlanConfig?.isCombo) {
                return false;
            }

            return true;
        });
    });

    // Unpaid level 4 (TDD-5603): every submenu link is inert except Mes factures.
    // The unpaid count badge also sits on "Factures", or the "Paramètres" one is unexplained once opened.
    const { isBlocked: isUnpaidBlocked, hasUnpaidInvoices, invoicesCount: unpaidInvoicesCount } = useUnpaidStatus();
    const isLinkLockedByUnpaid = (link: ChildrenItem): boolean => isUnpaidBlocked.value && link.to !== '/settings/invoices';
    const showsUnpaidBadge = (link: ChildrenItem): boolean => hasUnpaidInvoices.value && link.to === '/settings/invoices';
    const unpaidCountLabel = computed<string>(() => (unpaidInvoicesCount.value > 9 ? '9+' : String(unpaidInvoicesCount.value)));
    const { track: unpaidTrack, setInvoicesSource } = useUnpaidTracking();

    const handleLinkClick = (e: Event, link: ChildrenItem) => {
        if (isLinkLockedByUnpaid(link)) {
            e.preventDefault();
            e.stopPropagation();
            unpaidTrack('unpaid_locked_item_clicked', { module: link.to });
            return;
        }

        if (showsUnpaidBadge(link)) setInvoicesSource('sidebar_badge');

        if (link.permission && isUpsellRestricted(link.permission)) {
            // Stop the click in the capture phase so the inner NuxtLink never navigates;
            // a plain preventDefault on this wrapper wouldn't stop the link itself.
            e.preventDefault();
            e.stopPropagation();
            // The modal tracks the upsell exposure on open (type: 'nav', label: link name).
            useEvent('upsell:modal:open', { permissionKey: link.permission, type: 'nav', label: t(link.label), crossFamily: isCrossFamily(link.permission) });
        }
    };
</script>

<template>
    <ul>
        <li
            v-for="(link, index) in content"
            :key="index"
        >
            <div
                class="flex items-center"
                :class="{ 'opacity-40 cursor-not-allowed': isLinkLockedByUnpaid(link) }"
                @click.capture="(e: Event) => handleLinkClick(e, link)"
            >
                <LayoutSidebarLink
                    v-can="link.permission"
                    :to="link.to"
                    :label="$t(link.label)"
                    :extra-active-path="link.extraActivePath"
                    class="flex-1"
                >
                    <template
                        v-if="(link.permission && isUpsellRestricted(link.permission)) || showsUnpaidBadge(link)"
                        #after
                    >
                        <!-- mr-1 lines the badge up with the module-level badges: submenu links
                             have 8px right padding vs the modules' 12px (pr-3), so +4px matches. -->
                        <CommonUpsellBadge
                            v-if="link.permission && isUpsellRestricted(link.permission)"
                            variant="lightning"
                            class="ml-auto mr-1 shrink-0"
                        />
                        <span
                            v-else
                            class="ml-auto mr-1 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-statistics-red px-1.5 text-xs font-semibold leading-none text-white"
                            :aria-label="$t('settings.subscription.unpaid.nav.badge', { count: unpaidInvoicesCount })"
                        >
                            {{ unpaidCountLabel }}
                        </span>
                    </template>
                </LayoutSidebarLink>
            </div>
        </li>
    </ul>
</template>
